import { SelectChangeEvent } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ruRU } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import { TEditAppealTimeSlot, TTimeSlotListItem } from 'feature/appeals/type'
import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useActions } from 'store/hooks'
import { DatePicker, MenuItem, SelectDropdown } from 'ui'
import 'dayjs/locale/ru'

import style from './AppealTimeSlot.module.scss'

type AppealTimeSlotProps = {
  timeSlotsList: TTimeSlotListItem[]
  currentTimeSlot: TEditAppealTimeSlot
  setSubmitButtonDisabled: (value: boolean) => void
}

// таймслот заявки
export function AppealTimeSlot({
  timeSlotsList,
  currentTimeSlot,
}: AppealTimeSlotProps) {
  const [isSlotDisabled, setSlotDisabled] = useState<boolean>(false)
  const { control, setValue, setError, clearErrors } = useFormContext()
  const { date, id, key } = currentTimeSlot
  const parsedDate = dayjs(date)

  const today = dayjs()
  const yearLatter = dayjs().add(1, 'year')

  const { updateTimeSlot } = useActions()

  const isAvailableDate = (value: Date) => {
    return dayjs(value).isBetween(dayjs(), dayjs().add(2, 'year'), 'day', '[]')
  }

  const isValidDate = (value: Date) => {
    return dayjs(value).isValid()
  }

  const handlePickerChange = (newDate: Date) => {
    if (dayjs(newDate) < today || newDate === null) {
      setError(`date-${key}`, {})
      return
    }
    setValue(`date-${key}`, newDate)

    updateTimeSlot({
      key: key,
      date: isValidDate(newDate) ? dayjs(newDate).format('YYYY-MM-DD') : null,
      isValidDate:
        isAvailableDate(newDate) && isValidDate(newDate) ? true : false,
    })

    clearErrors(`date-${key}`)
  }

  const handleSlotChange = (event: SelectChangeEvent<unknown>) => {
    const slotId = event.target.value
    setValue(`time-${key}`, slotId)
    setSlotDisabled(false)
    clearErrors(`time-${key}`)

    if (typeof slotId === 'number' || typeof slotId === 'string') {
      updateTimeSlot({
        key: key,
        id: +slotId,
      })
    }
  }

  useEffect(() => {
    setSlotDisabled(
      timeSlotsList.some(slot => slot.id === id && slot.disabled === true),
    )
  }, [timeSlotsList])

  useEffect(() => {
    if (isSlotDisabled) {
      setError(`time-${key}`, {
        type: 'slotNotActive',
        message: 'Временной слот неактуален, выберите другой',
      })
      return
    }
  }, [isSlotDisabled])

  return (
    <div className={style.dateParentWrap}>
      <div className={style.dateWrapper}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale='ru'
          localeText={
            ruRU.components.MuiLocalizationProvider.defaultProps.localeText
          }
        >
          <Controller
            name={`date-${key}`}
            control={control}
            defaultValue={parsedDate}
            render={({ field: { onChange, ...restField }, fieldState }) => (
              <DatePicker
                className={style.datePicker}
                yearsPerRow={3}
                slotProps={{
                  field: { clearable: true },
                  actionBar: {
                    actions: ['today', 'clear'],
                  },
                  textField: {
                    required: true,
                    error: !!fieldState?.error,
                  },
                }}
                onChange={handlePickerChange}
                views={['year', 'month', 'day']}
                minDate={today}
                maxDate={yearLatter}
                label='Дата'
                {...restField}
              />
            )}
          />
        </LocalizationProvider>
      </div>
      <Controller
        name={`time-${key}`}
        control={control}
        defaultValue={`${id}`}
        render={({ field: { onChange, ...restField } }) => (
          <div className={style.dateWrapper}>
            <SelectDropdown
              selectProps={{
                ...restField,
                label: 'Время',
                onChange: handleSlotChange,
              }}
              helperText={
                isSlotDisabled
                  ? 'Временной слот неактуален, выберите другой'
                  : ''
              }
              formControlProps={{ sx: { minWidth: '100%' } }}
            >
              {timeSlotsList.map(timeSlot => (
                <MenuItem
                  value={timeSlot.id === 0 ? '0' : timeSlot.id}
                  key={timeSlot.id}
                  disabled={timeSlot.disabled}
                >
                  {`c ${timeSlot.from} до ${timeSlot.to}`}
                </MenuItem>
              ))}
            </SelectDropdown>
          </div>
        )}
      />
    </div>
  )
}
